import React, { useEffect, useState } from "react"
import { SensaiComponentCardsPrice } from "../../../gql/generated/graphql"
import styled from "styled-components"
import { Box } from "@mui/system"
import { useMediaQuery } from "@mui/material"
import { Link, navigate } from "gatsby"
import { Button } from "../../ui/Button"
import { useCheckoutDispatch, useCheckoutState } from "../../../checkout/state"
import theme from "../../../material-ui/theme"
import { formatCurrencyString } from "../../../utils/format"
import { isSubscriptionInCart } from "../../../checkout/shared"
import { Currency } from "../../../checkout/types/checkoutTypes"
import { findProduct, useProducts } from "../../../hooks/useProducts"
import { Product } from "../../../checkout/product"
import { useAuthState } from "../../../contexts/authContext"
import { useCurrentUser } from "../../../hooks/useCurrentUser"
import { setLs } from "../../../utils/localStorage"
import { addItemToCart } from "../../../checkout/events"
import ProSignup from "../../auth/ProSignup"
import LoadingSpinner from "../../ui/LoadingSpinner"

type PageState = "NOT_LOGGED_IN" | "LOGGED_IN" | "LOADING"

const TitleContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  margin-bottom: 16px;
`

const Title = styled.h2<{ isMobile: boolean }>`
  text-align: center;
  font-family: Rubik;
  font-size: ${isMobile => (isMobile ? "24px" : "26px")};
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 107.692% */
  letter-spacing: 0.26px;

  background: linear-gradient(90deg, #1d99b8 0%, #5c38bd 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const FooterText = styled.div<{ isMobile: boolean; dark: boolean }>`
  color: ${props => (props.dark ? "white" : "#454545")};
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.24px;
  margin-top: -1.25rem;
  margin-bottom: 1rem;
  min-height: ${props => (props.isMobile ? "0" : "28px")};
  position: absolute;
  bottom: -0.5rem;
  padding: 0 2em;
  left: 0;
  right: 0;
  text-align: center;
`

const Bullets = styled.div`
  width: 100%;
  justify-content: flex-start;
  margin-bottom: 14px;
  li {
    margin-bottom: 0.5rem;
  }
`

const BulletText = styled.span<{ isMobile: boolean; dark: boolean }>`
  color: ${props => (props.dark ? "white" : "#454545")};
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.2px;
`

const Emphasized = styled.div<{ isMobile: boolean }>`
  color: #b632b0;
  text-align: center;
  font-family: Rubik;
  font-size: ${props => (props.isMobile ? "16px" : "18px")};
  font-style: normal;
  font-weight: 600;
  line-height: 39px; /* 177.273% */
  letter-spacing: 0.22px;
`

const TextContainer = styled.div<{ isMobile: boolean }>`
  margin: 0 1rem;
  text-align: center;
`

const PriceCard = ({ card }: { card: SensaiComponentCardsPrice }) => {
  const {
    title,
    cardStyle,
    product: prd,
    bundledProduct,
    priceSubTextOverride,
    bulletPoints,
    highlight,
    featuredText,
    buttonText,
    footerText,
    couponCode,
  } = card
  const dark = cardStyle?.style === "dark"

  const checkoutState = useCheckoutState()
  const checkoutDispatch = useCheckoutDispatch()
  const { cartItems, currency } = checkoutState

  const [showCartLimit, setCartLimit] = useState<boolean>(
    isSubscriptionInCart(cartItems)
  )

  useEffect(() => {
    setCartLimit(isSubscriptionInCart(cartItems))
  }, [cartItems])

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  const authState = useAuthState()
  const { user, fetch } = useCurrentUser()
  const [pageStage, setPageState] = useState<PageState>("LOADING")
  const [personalMembershipLimit, setPersonalMembershipLimit] = useState<
    boolean
  >(false)
  const [proMembershipLimit, setProMembershipLimit] = useState<boolean>(false)

  const [showProSignup, setShowProSignup] = useState<boolean>(false)
  const [pendingPro, setPeningPro] = useState<boolean>(false)

  const products = useProducts()

  const product = prd ? findProduct(products, prd!.id) : null

  const cardProductBundled = bundledProduct
    ? findProduct(products, bundledProduct.id)
    : undefined

  const isAnnual = product?.isYearly() ?? false
  const forceBundledProduct = product?.getForcedProductBundleId()
    ? findProduct(products, product?.getForcedProductBundleId()!)
    : null

  const hasBullets = (!!bulletPoints || forceBundledProduct?.isUsage()) ?? false

  const internalFreeLink = !product

  const handleBuySubscription = () => {
    if (!product) {
      return
    }

    if (product.isPro && !user?.hasProConfig()) {
      setPeningPro(true)
      setShowProSignup(true)
      return
    }
    if (couponCode) {
      let expiry = new Date()

      expiry.setDate(expiry.getDate() + 10)

      setLs("coupon", {
        id: couponCode,
        expiry: expiry.toISOString(),
      })
    }

    addItemToCart(
      checkoutState,
      checkoutDispatch,
      1,
      cardProductBundled
        ? [product, cardProductBundled] //the order is important
        : [product],
      products
    )

    navigate("/store/checkout")
  }

  useEffect(() => {
    if (authState.isLoggedIn() && !user) {
      setPageState("LOADING")
    } else if (user) {
      setPageState("LOGGED_IN")
      setPersonalMembershipLimit(user.hasPersonalMembership())
      setProMembershipLimit(user.hasProfessionalMembership())
    } else {
      setPageState("NOT_LOGGED_IN")
      setPersonalMembershipLimit(false)
      setProMembershipLimit(false)
    }
  }, [user, authState])

  useEffect(() => {
    if (user && user.hasProConfig() && pendingPro && showProSignup) {
      handleBuySubscription()
    }
  }, [user])

  const getPricePerMonth = (
    product: Product,
    currency: Currency,
    additionalProduct?: Product | null
  ) => {
    let amt = product.getPriceAmt(currency, true, true) ?? 0
    if (additionalProduct) {
      amt += additionalProduct.getPriceAmt(currency, false, true) ?? 0
    }
    const dividor = product.isYearly() ? 12 : 1

    return formatCurrencyString(
      { value: amt / dividor, currency: currency.toUpperCase() },
      false
    )
  }

  const getAnnualPricePerYear = (product: Product, currency: Currency) => {
    const amt = product.getPriceAmt(currency, true, true) ?? 0
    return formatCurrencyString(
      { value: amt, currency: currency.toUpperCase() },
      false
    )
  }

  let priceText = "0"
  let priceSubText = ""
  if (product) {
    priceText = getPricePerMonth(product, currency, cardProductBundled)

    if (product?.isYearly()) {
      priceSubText = `($${getAnnualPricePerYear(
        product,
        currency
      )} billed yearly)`
    } else if (product?.isMonthly()) {
      if (product?.hasUpfrontNumCycles()) {
        priceSubText =
          "then " +
          product?.getProductBillingDescription({
            currency,
            fullDescription: false,
            couponDiscountDays: 0,
            trialDays: 0,
          })
      }
    }
  }
  if (priceSubTextOverride) {
    priceSubText = priceSubTextOverride
  }

  const renderActiveMember = (isPro = false) => (
    <TextContainer isMobile={isMobile}>
      <div style={{ textAlign: "center" }}>
        <p>
          You have an active {isPro ? "professional " : ""}membership, please{" "}
          <Link to="/user">see your account</Link> for more details.
        </p>
      </div>
    </TextContainer>
  )
  const renderMembershipInCart = () => (
    <TextContainer isMobile={isMobile}>
      <div style={{ textAlign: "center" }}>
        <p>
          You already have a membership in your cart. {` `}
          <Link to="/store/checkout">View your cart.</Link>
        </p>
      </div>
    </TextContainer>
  )

  return (
    <>
      <ProSignup
        setModalOpen={setShowProSignup}
        modalOpen={showProSignup}
        onComplete={() => {
          //refetch the user
          fetch({})
        }}
      />
      <Box
        data-aos="flip-left"
        sx={{
          padding: "1.5rem",
          paddingBottom: "3rem",
          borderRadius: "1rem",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundColor: dark
            ? "var(--color-dark-blue)"
            : "var(--color-bg-light)",
          ...(dark ? { color: "white" } : {}),
          width: "100%",

          ...(dark
            ? { boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)" }
            : { boxShadow: "0px 0px 4px #9a9a9a" }),
          ...(highlight
            ? { border: "2px solid #1d99b8", boxShadow: "0px 0px 4px #1d99b8" }
            : {}),
        }}
      >
        {pageStage === "LOADING" ? (
          <LoadingSpinner />
        ) : (
          <>
            <Emphasized isMobile={isMobile}>
              {featuredText ? featuredText : <>&emsp;</>}
            </Emphasized>

            <Box
              sx={{
                color: dark ? "white" : "#454545",
                textAlign: "center",
                fontFamily: "Rubik",
                fontSize: "26px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "28px",
                letterSpacing: "0.26px",
                margin: "1.5rem",
              }}
            >
              <Box
                sx={{
                  color: dark ? "white" : "#454545",
                  fontFamily: "Rubik",
                  fontSize: isMobile ? "30px" : "32px",
                  fontStyle: "normal",
                  fontWeight: "500",
                  lineHeight: "28px",
                  letterSpacing: "0.32px",
                  display: "inline",
                }}
              >
                $
              </Box>
              <Box
                sx={{
                  color: dark ? "white" : "#454545",
                  fontFamily: "Rubik",
                  fontSize: isMobile ? "56px" : "60px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "28px",
                  letterSpacing: "0.64px",
                  display: "inline",
                }}
              >
                {priceText}
              </Box>
              {product?.isRecurring() &&
                !product?.hasUpfrontNumCycles() &&
                "/month"}
              {product?.isRecurring() &&
                product?.hasUpfrontNumCycles() &&
                " today"}
            </Box>
            <Box
              sx={{
                color: dark ? "white" : "#666666",
                fontFamily: "Rubik",
                fontSize: isMobile ? "14px" : "16px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "28px",
                letterSpacing: "0.24px",
                display: "inline",
                marginTop: "-1.25rem",
                marginBottom: "1rem",
                minHeight: isMobile ? "0" : "28px",
              }}
            >
              {" "}
              {priceSubText}
            </Box>
            <TitleContainer isMobile={isMobile}>
              <Title isMobile={isMobile}>{title || product?.name}</Title>
            </TitleContainer>

            {hasBullets && bulletPoints!.length === 1 && (
              <Bullets>
                {bulletPoints!.map((bullet, i) => (
                  <Box sx={{ padding: "0 5px" }} key={i}>
                    <BulletText isMobile={isMobile} dark={dark}>
                      {forceBundledProduct
                        ? forceBundledProduct.replaceUsageDescription(
                            bullet?.text ?? "",
                            currency
                          )
                        : bullet?.text}
                    </BulletText>
                  </Box>
                ))}
              </Bullets>
            )}
            {hasBullets && bulletPoints!.length > 1 && (
              <Bullets>
                <ul>
                  {bulletPoints!.map((bullet, i) => (
                    <li key={i}>
                      {forceBundledProduct
                        ? forceBundledProduct.replaceUsageDescription(
                            bullet?.text ?? "",
                            currency
                          )
                        : bullet?.text}
                    </li>
                  ))}
                </ul>
              </Bullets>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "auto",
                marginBottom: "1px",
              }}
            >
              {(!product?.isRecurring() ||
                (!(
                  product?.isPersonalMembership() && personalMembershipLimit
                ) &&
                  !(
                    product?.isProfessionalMembership() && proMembershipLimit
                  ) &&
                  !showCartLimit)) && (
                <Button
                  type={highlight ?? false ? "primary" : "primaryOutline"}
                  title={
                    product?.isPro && !(user?.hasProConfig() ?? false)
                      ? "GET STARTED"
                      : buttonText ?? "ADD TO CART"
                  }
                  sectionId="membership"
                  id={
                    product?.isPro
                      ? "continue_pro_signup"
                      : !product
                      ? "continue_free_app"
                      : isAnnual
                      ? "continue_yearly"
                      : "continue_monthly"
                  }
                  link={
                    internalFreeLink
                      ? [
                          {
                            url: "/app",
                          },
                        ]
                      : undefined
                  }
                  onClick={() => {
                    if (internalFreeLink) {
                      return
                    }

                    handleBuySubscription()
                  }}
                  disabled={false}
                />
              )}
              {personalMembershipLimit &&
                !showCartLimit &&
                product?.isPersonalMembership() &&
                renderActiveMember()}
              {proMembershipLimit &&
                !showCartLimit &&
                product?.isProfessionalMembership() &&
                renderActiveMember(true)}
              {showCartLimit &&
                product?.isRecurring() &&
                renderMembershipInCart()}
            </Box>
            {footerText && (
              <FooterText isMobile={isMobile} dark={dark}>
                {footerText}
              </FooterText>
            )}
          </>
        )}
      </Box>
    </>
  )
}

export default PriceCard
